import { useEffect, useCallback, useState } from "react";
import { usePreviewBrowserContext } from "./PreviewBrowserContext";

export function usePreviewBrowser(workspaceId: string) {
  const ctx = usePreviewBrowserContext();

  // Reactive state — updated via the event bus, not props
  const [currentPath, setCurrentPath] = useState(() => ctx.getCurrentPath(workspaceId));
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);

  // Subscribe to this workspace's events
  useEffect(() => {
    // Driver → tab: open/swap to a file
    const onNavigate = (filePath: string) => {
      // Convert DB path to URL path for display
      const prefix = `${workspaceId}/`;
      const urlPath = filePath.startsWith(prefix) ? `/${filePath.slice(prefix.length)}` : `/${filePath}`;
      setCurrentPath(urlPath);
    };

    // Tab → provider: iframe reported a navigation (via postMessage)
    const onNavigated = (path: string) => {
      setCurrentPath(path);
      setCanGoBack(true);
      setCanGoForward(true);
    };

    ctx.events.subscribe(workspaceId, "navigate", onNavigate);
    ctx.events.subscribe(workspaceId, "navigated", onNavigated);

    return () => {
      ctx.events.unsubscribe(workspaceId, "navigate", onNavigate);
      ctx.events.unsubscribe(workspaceId, "navigated", onNavigated);
    };
  }, [ctx, workspaceId]);

  return {
    currentPath,
    canGoBack,
    canGoForward,
    /** Emit a navigation report (called by the tab's postMessage listener) */
    emitNavigated: useCallback((path: string) => {
      ctx.events.emit[workspaceId].navigated(path);
    }, [ctx, workspaceId]),
    goBack: useCallback(() => ctx.goBack(workspaceId), [ctx, workspaceId]),
    goForward: useCallback(() => ctx.goForward(workspaceId), [ctx, workspaceId]),
    reload: useCallback(() => ctx.reload(workspaceId), [ctx, workspaceId]),
    setIframe: useCallback((iframe: HTMLIFrameElement | null) => ctx.setIframe(workspaceId, iframe), [ctx, workspaceId]),
  };
}
